'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useSignIn } from '@clerk/nextjs'
import { OnboardingLayout } from '@/features/onboarding'
import { ResetPasswordCodeStep } from '@/features/auth/components/reset-password-code-step'
import { ResetPasswordNewPasswordStep } from '@/features/auth/components/reset-password-new-password-step'
import { FieldDescription } from '@/components/ui/field'

type Step = 'enter-code' | 'set-password'

/**
 * Landed on from `InvestorForgotPasswordDialog` via client-side navigation (same tab, same
 * `signIn` resource in memory). Same shape as `AdminResetPasswordView` — code entry and
 * new-password entry are deliberately two separate components, never both sets of fields on
 * screen together — just with investor-facing copy/routes and `role="investor"`.
 */
export function InvestorResetPasswordView() {
  const router = useRouter()
  const { signIn } = useSignIn()
  const [step, setStep] = useState<Step>('enter-code')
  // See `AdminResetPasswordView`'s identical comment — set right before `finalizeAs()` runs so
  // the `!signIn.identifier` branch below is skipped during that reactive window.
  const [finalizing, setFinalizing] = useState(false)

  if (finalizing) {
    return (
      <OnboardingLayout title="Signing you in…" width="sm" fitContent>
        <FieldDescription>Just a moment…</FieldDescription>
      </OnboardingLayout>
    )
  }

  // No in-progress reset attempt for this identifier — e.g. navigated here directly, or the
  // attempt expired — so there's nothing to verify a code against.
  if (!signIn.identifier) {
    return (
      <OnboardingLayout title="Reset your password" width="sm" fitContent>
        <FieldDescription>
          We couldn&apos;t find an in-progress password reset.{' '}
          <button
            type="button"
            onClick={() => router.push('/login')}
            className="text-primary font-semibold underline-offset-4 hover:underline"
          >
            Start from the Login page
          </button>
          .
        </FieldDescription>
      </OnboardingLayout>
    )
  }

  if (step === 'set-password') {
    return (
      <OnboardingLayout
        title="Set your new password"
        description="Choose a new password for your account."
        width="sm"
        fitContent
      >
        <ResetPasswordNewPasswordStep role="investor" onFinalizing={() => setFinalizing(true)} />
      </OnboardingLayout>
    )
  }

  return (
    <OnboardingLayout
      title="Reset your password"
      description="Enter the code we emailed you."
      width="sm"
      fitContent
    >
      <ResetPasswordCodeStep email={signIn.identifier} onVerified={() => setStep('set-password')} />
    </OnboardingLayout>
  )
}
